import React, { Component } from "react";
import PropTypes from "prop-types";
import { CtrlCollection } from "@yuigoto/ctrl";

import { 
  AuthLoginControl, 
  AuthPasswordControl, 
  AuthRegisterControl 
} from "components/login/UserAuthControls";
import { NextControlRenderer } from "components/renderers/NextControlRenderer";
import { GlamboxLoginHandler } from "core/glambox/GlamboxLoginHandler";
import { ExecuteRequest } from "core/glambox/GlamboxApi";
import { GlamboxEndpoints } from "core/glambox/GlamboxEndpoints";

// STATUS

const VIEW_LOGIN            = 0;
const VIEW_RECOVERY         = 1;
const VIEW_REGISTER         = 2;

const LOGIN_STATUS_OK       = 3;
const LOGIN_STATUS_FAIL     = 4;

const RECOVERY_STATUS_OK    = 5;
const RECOVERY_STATUS_FAIL  = 6;

const REGISTER_STATUS_OK    = 7;
const REGISTER_STATUS_FAIL  = 8;

const PENDING_LOGIN         = 9;
const PENDING_RECOVERY      = 10;
const PENDING_REGISTER      = 11;

class UserAuthView extends Component {
  state = {
    step: 0,
    message: null
  };

  /**
   * Handler de login.
   * 
   * @type {GlamboxLoginHandler}
   */
  loginHandler = null;

  /**
   * @type {CtrlCollection}
   */
  loginCollection     = null;

  /**
   * @type {CtrlCollection}
   */
  recoveryCollection  = null;

  /**
   * @type {CtrlCollection}
   */
  registerCollection  = null;

  // --------------------------------------------------------------------

  constructor (props) {
    super(props);

    this.loginHandler = new GlamboxLoginHandler(
      process.env.GB_API_ROOT, 
      "/login",
      process.env.GB_COOKIE_ROOT,
    );

    this.loginCollection = new CtrlCollection();
    for (let n = 0; n < AuthLoginControl.length; n++) {
      this.loginCollection.add(AuthLoginControl[n]);
    }

    this.recoveryCollection = new CtrlCollection();
    for (let n = 0; n < AuthPasswordControl.length; n++) {
      this.recoveryCollection.add(AuthPasswordControl[n]);
    }

    this.registerCollection = new CtrlCollection();
    for (let n = 0; n < AuthRegisterControl.length; n++) {
      this.registerCollection.add(AuthRegisterControl[n]);
    }
  }

  // LIFECYCLE
  // --------------------------------------------------------------------
  
  componentDidMount () {
    if (this.loginHandler.isUserLoggedIn()) {
      if (typeof this.props.onExecutionFinish === "function") {
        this.props.onExecutionFinish();
      }
    }
  }

  // EXECUÇÃO
  // --------------------------------------------------------------------

  /**
   * @param {Event} e
   */
  _finishExecution = (e) => {
    e.preventDefault();

    this.props.onExecutionFinish();
  };

  /**
   * @param {Event} e
   */
  _executeLogin = (e) => {
    e.preventDefault();

    this.setState({
      step: PENDING_LOGIN,
      message: null
    }, () => {
      this._requestLogin();
    });
  };

  /**
   * @param {Event} e
   */
  _executeRecovery = (e) => {
    e.preventDefault();

    this.setState({
      step: PENDING_RECOVERY,
      message: null
    }, () => {
      this._requestRecovery()
    });
  };

  /**
   * @param {Event} e
   */
  _executeRegistration = (e) => {
    e.preventDefault();
    e.preventDefault();

    this.setState({
      step: PENDING_REGISTER,
      message: null
    }, () => {
      this._requestRegistration()
    });
  };

  _requestLogin = () => {
    const {
      props
    } = this;

    if (!this.loginCollection.validate()) {
      this.setState({
        step: VIEW_LOGIN
      });

      this.forceUpdate();
    } else {
      let {
        username,
        password
      } = this.loginCollection.toObject();

      this.loginHandler.authenticate(
        username, 
        password,
        (response) => {
          const {
            result,
            statusCode
          } = response;

          if (statusCode === 200) {
            props.onExecutionFinish();
          } else {
            this.setState({
              step: LOGIN_STATUS_FAIL,
              message: (result && result.message) 
                ? result.message 
                : "Não foi possível efetuar login em sua conta. Por favor, verifique o endereço de e-mail e/ou senha digitados."
            });
          }
        }
      );
    }
  };

  _requestRecovery = () => {
    if (!this.recoveryCollection.validate()) {
      this.setState({
        step: VIEW_RECOVERY
      });
    } else {
      let {
        email
      } = this.recoveryCollection.toObject();

      ExecuteRequest(
        GlamboxEndpoints.Open.forgotPassword,
        {
          email
        },
        "POST",
        null,
        null,
        (response) => {
          const {
            result,
            statusCode
          } = response;

          if (statusCode === 200) {
            this.setState({
              step: RECOVERY_STATUS_OK,
              message: (result && result.message) 
                ? result.message 
                : `As instruções para recuperação de senha foram enviadas para o seu endereço de e-mail <strong>${email}</strong>.`
            });
          } else {
            this.setState({
              step: RECOVERY_STATUS_FAIL,
              message: (result && result.message) 
                ? result.message.replace('"', "") 
                : "Endereço de e-mail não encontrado, por favor verifique o endereço de e-mail fornecido."
            });
          }
        }
      );
    }
  };

  _requestRegistration = () => {
    const {
      props
    } = this;

    if (!this.registerCollection.validate()) {
      this.setState({
        step: VIEW_REGISTER
      });
    } else {
      let requestData = this.registerCollection.toObject();

      if (requestData.password !== requestData.passwordConfirmation) {
        this.registerCollection.invalidate(
          "password",
          "As senhas fornecidas não conferem."
        );

        this.registerCollection.invalidate(
          "passwordConfirmation",
          "As senhas fornecidas não conferem."
        );

        this.setState({
          step: REGISTER_STATUS_FAIL,
          message: null
        });
      }

      ExecuteRequest(
        GlamboxEndpoints.Open.registerSimple,
        requestData,
        "POST",
        null,
        null,
        (response) => {
          const {
            result,
            statusCode
          } = response;

          if (statusCode === 200) {
            this.loginHandler.authenticate(
              requestData.email, 
              requestData.password,
              (response) => {
                const {
                  result,
                  statusCode
                } = response;
      
                if (statusCode === 200) {
                  props.onExecutionFinish();
                } else {
                  this.setState({
                    step: LOGIN_STATUS_FAIL,
                    message: (result && result.message) 
                      ? result.message 
                      : "Não foi possível efetuar login em sua conta. Por favor, verifique o endereço de e-mail e/ou senha digitados."
                  });
                }
              }
            );
          } else {
            this.setState({
              step: REGISTER_STATUS_FAIL,
              message: (result && result.message) 
                ? result.message.replace('"', "") 
                : "Não foi possível registrar o usuário, por favor verifique os dados e tente novamente."
            });
          }
        }
      );
    }
  };

  // STATUS
  // --------------------------------------------------------------------

  _renderLoader = () => {
    const {
      state
    } = this;

    let message;
    switch (state.step) {
      case PENDING_LOGIN:
        message = "Efetuando login...";
        break;
      case PENDING_RECOVERY:
        message = "Enviando solicitação...";
        break;
      case PENDING_REGISTER:
        message = "Enviando dados e executando login...";
        break;
      default:
        message = "Carregando...";
        break;
    }

    return (
      <div className={"gb-pending"}>
        <div className={"gb-pending__over"}>
          <p className={"text-center"}>
            <i className={"fa fa-spin fa-circle-notch fa-5x"}/>
          </p>
          <p className={"lead text-center"}>{message}</p>
        </div>
      </div>
    );
  };

  _setCurrentStep = (step) => {
    return (e) => {
      e.preventDefault();

      this.setState({
        step: step,
        message: null
      });
    };
  };

  // VIEWS
  // --------------------------------------------------------------------

  _viewLogin = () => {
    const {
      state
    } = this;

    let errorMessage = (state.message) 
      ? (<p className={"gb-auth-error"}>{state.message}</p>)
      : null;

    return (
      <div className={"gb-login"}>
        <div className={"gb-login__info"}>
          <h4 className={"pb-1"}>BEM-VINDA!</h4>

          <p className={"pb-3"}>Receba as últimas novidades sobre beleza e bem-estar, além de promoções exclusivas e sorteios.</p>

          <button className={"btn btn-sm btn-outline-white"} 
                  onClick={this._setCurrentStep(VIEW_REGISTER)}>
            <strong>
              REGISTRAR-SE
            </strong>
          </button>
        </div>

        <div className={"gb-login__auth"}>
          <h4 className={"pb-1"}>JÁ É CADASTRADA?</h4>

          <form className={"pb-3"} onSubmit={this._executeLogin}>
            {NextControlRenderer.renderCollection(this.loginCollection)}

            {errorMessage}

            <button type={"submit"} 
                    className={"btn btn-light-lime"}>
              <strong>ENTRAR</strong>
            </button>

            <button type={"submit"} 
                    className={"btn btn-sm btn-facebook"}>
              <i className={"fab fa-facebook fa-2x"}/>
              <strong>
                CONTINUAR COM O FACEBOOK
              </strong>
            </button>
          </form>

          <button type={"button"} 
                  onClick={this._setCurrentStep(VIEW_RECOVERY)}
                  className={"btn btn-xs btn-outline-white"}>
            <strong>
              Esqueci minha senha
            </strong>
          </button>
        </div>
      </div>
    );
  };

  _viewRecovery = () => {
    const {
      state
    } = this;

    let errorMessage = (state.message && state.step === RECOVERY_STATUS_FAIL) 
          ? (<p className={"gb-auth-error"}>{state.message}</p>)
          : null,
        successMessage = (state.message && state.step === RECOVERY_STATUS_OK) 
          ? (<p className={"gb-auth-success"}>{state.message}</p>)
          : null;

    let buttons = (state.step === RECOVERY_STATUS_OK) 
      ? (
        <>
          <button type={"button"} 
                  className={"btn btn-light-lime"} 
                  onClick={this._finishExecution}>
            <strong>
              Fechar
            </strong>
          </button>
        </>
      ) 
      : (
        <>
          <button className={"btn btn-light-lime"} type={"submit"}>
            <strong>
              Redefinir
            </strong>
          </button>

          <button onClick={this._setCurrentStep(VIEW_LOGIN)} 
                  type={"button"}
                  className={"btn btn-sm btn-outline-gray"}>
            <strong>
              Retornar
            </strong>
          </button>
        </>
      );

    return (
      <div className={"gb-recovery"}>
        <div className={"gb-recovery__info"}>
          <h4>Olá Glamgirl,</h4>
          <p>Se você esqueceu a sua senha, não tem problema. Por favor, nos diga o e-mail da sua conta para iniciar o processo.</p>
        </div>
        <div className={"gb-recovery__form"}>
          <h5 className={"text-center text-lg-left"}>Redefinição de senha</h5>

          <form onSubmit={this._executeRecovery}>
            {NextControlRenderer.renderCollection(this.recoveryCollection)}

            {successMessage}

            {errorMessage}

            {buttons}
          </form>
        </div>
      </div>
    );
  };

  _viewRegistration = () => {
    const {
      state
    } = this;

    let errorMessage = (state.message && state.step === REGISTER_STATUS_FAIL) 
          ? (<p className={"gb-auth-error"}>{state.message}</p>)
          : null,
        successMessage = (state.message && state.step === REGISTER_STATUS_OK) 
          ? (<p className={"gb-auth-success"}>{state.message}</p>)
          : null;

    let buttons = (state.step === RECOVERY_STATUS_OK) 
      ? (
        <>
          <button type={"button"} 
                  className={"btn btn-light-lime"} 
                  onClick={this._finishExecution}>
            <strong>
              Fechar
            </strong>
          </button>
        </>
      ) 
      : (
        <>
          <button className={"btn btn-light-lime"} type={"submit"}>
            <strong>
              Enviar
            </strong>
          </button>

          <button onClick={this._setCurrentStep(VIEW_LOGIN)} 
                  type={"button"}
                  className={"btn btn-sm btn-outline-white"}>
            <strong>
              Retornar
            </strong>
          </button>
        </>
      );
          
    return (
      <div className={"gb-register"}>
        <form onSubmit={this._executeRegistration}>
          <h4 className={"text-center"}>REGISTRE-SE!</h4>

          {NextControlRenderer.renderCollection(this.registerCollection)}

          {successMessage}

          {errorMessage}

          {buttons}
        </form>
      </div>
    );
  };

  // --------------------------------------------------------------------

  render () {
    const {
      state,
      props
    } = this;

    switch (state.step) {
      case 0:
        return this._viewLogin();
      case 1:
        return this._viewRecovery();
      case 2:
        return this._viewRegistration();
      case 3:
      case 4:
        return this._viewLogin();
      case 5:
      case 6:
        return this._viewRecovery();
      case 7:
      case 8:
        return this._viewRegistration();
      case 9:
      case 10:
      case 11:
        return this._renderLoader();
      default:
        return null;
    }
  }
}

UserAuthView.propTypes = {
  onExecutionFinish: PropTypes.func.isRequired
};

export {
  UserAuthView
};
